import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaCog, FaRocket, FaClock, FaPlus, FaTrash, FaExclamationCircle, FaHandPointer, FaCheckCircle } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';

const ModifyScanModal = ({ isOpen, onClose, strategyType, executionMode = 'automatic', onStartScan, onScheduleCreated, hideSessionExpiredError = false }) => {
  const isBreakout = strategyType === 'breakout';
  
  // Form state with default values
  const [maxInvestment, setMaxInvestment] = useState(50000);
  const [quantity, setQuantity] = useState(1);
  const [targetPercent, setTargetPercent] = useState(10);  // 10% target for automatic mode
  const [stopLossPercent, setStopLossPercent] = useState(2);  // 2% stop-loss for automatic mode
  const [momentumMin, setMomentumMin] = useState(3);
  const [momentumMax, setMomentumMax] = useState(5);
  const [rsiMin, setRsiMin] = useState(25);
  const [rsiMax, setRsiMax] = useState(60);
  const [optionMoneyness, setOptionMoneyness] = useState('ATM');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleTimes, setScheduleTimes] = useState(['09:30']); // 9:30 AM default
  const [selectedMode, setSelectedMode] = useState(executionMode); // 'automatic' or 'manual'
  
  // Broker connection state
  const [connectedBrokers, setConnectedBrokers] = useState([]);
  const [brokerLoading, setBrokerLoading] = useState(false);
  const [brokerError, setBrokerError] = useState('');

  const resetForm = useCallback(() => {
    setMaxInvestment(50000);
    setQuantity(1);
    setTargetPercent(10);  // 10% target for automatic mode
    setStopLossPercent(2);  // 2% stop-loss for automatic mode
    setMomentumMin(3);
    setMomentumMax(5);
    setRsiMin(25);
    setRsiMax(60);
    setOptionMoneyness('ATM');
    setScheduleEnabled(false);
    setScheduleTimes(['09:30']);
    setSelectedMode(executionMode); // Reset to passed mode
  }, [executionMode]);

  // Fetch broker connections when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBrokers();
      resetForm();
    }
  }, [isOpen, resetForm]);
  
  const fetchBrokers = async () => {
    try {
      setBrokerLoading(true);
      setBrokerError('');
      const response = await api.get('/api/v1/brokers/');
      const activeBrokers = response.data.filter(b => b.is_active);
      
      if (activeBrokers.length === 0) {
        setBrokerError('No active broker connections found. Please connect your broker account first.');
        setConnectedBrokers([]);
        return;
      }
      
      // Check if broker has valid access token (generated today for Zerodha)
      const brokersWithValidToken = activeBrokers.filter(broker => {
        if (!broker.last_token_generated_at) {
          return false; // No token generated
        }
        
        // Check if token was generated today (Zerodha tokens expire daily)
        const tokenDate = new Date(broker.last_token_generated_at);
        const today = new Date();
        const isToday = tokenDate.toDateString() === today.toDateString();
        
        return isToday;
      });
      
      if (brokersWithValidToken.length === 0) {
        // Broker connected but token expired or not generated
        setBrokerError('Your broker access token has expired. Zerodha tokens expire daily and need to be regenerated. Please reconnect your broker account.');
        setConnectedBrokers([]); // Mark as no valid brokers
      } else {
        setConnectedBrokers(brokersWithValidToken);
      }
    } catch (err) {
      console.error('Failed to fetch broker connections:', err);
      setBrokerError('Failed to fetch broker connections. Please try again.');
      setConnectedBrokers([]);
    } finally {
      setBrokerLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if broker is connected with valid token before proceeding
    if (connectedBrokers.length === 0) {
      if (brokerError.includes('expired')) {
        alert('Your broker access token has expired. Please reconnect your Zerodha account to generate a new token. Zerodha tokens expire daily at midnight.');
      } else {
        alert('Please connect your broker account before scheduling or starting a scan. The scan requires access to live market data from your broker.');
      }
      return;
    }
    
    const scanParams = {
      max_investment: parseFloat(maxInvestment),
      quantity: parseInt(quantity),
      target_percent: parseFloat(targetPercent),
      stop_loss_percent: parseFloat(stopLossPercent),
      momentum_threshold_min: parseFloat(momentumMin),
      momentum_threshold_max: parseFloat(momentumMax),
      rsi_min: parseFloat(rsiMin),
      rsi_max: parseFloat(rsiMax),
      option_moneyness: optionMoneyness
    };
    
    // If scheduling is enabled, create scheduled scan
    if (scheduleEnabled && scheduleTimes.length > 0) {
      try {
        console.log('📅 Creating scheduled scan with params:', scanParams);
        
        const schedulePayload = {
          scan_type: strategyType,
          schedule_times: scheduleTimes,
          parameters: {
            ...scanParams,
            execution_mode: selectedMode
          }
        };
        
        const response = await api.post('/api/v1/fno/schedule-scan', schedulePayload);
        console.log('✅ Scheduled scan(s) created:', response.data);
        
        // Response is an array of created scans
        const scanCount = Array.isArray(response.data) ? response.data.length : 1;
        alert(`Scheduled scan created successfully! Created ${scanCount} schedule(s) for times: ${scheduleTimes.join(', ')}`);
        
        // Notify parent to refresh scheduled scans list
        if (onScheduleCreated) {
          onScheduleCreated();
        }
      } catch (error) {
        console.error('❌ Failed to create scheduled scan:', error);
        alert(`Failed to create scheduled scan: ${error.response?.data?.detail || error.message}`);
      }
    } else {
      // Run scan immediately (pass mode to parent)
      console.log(`📊 Starting custom scan in ${selectedMode} mode with params:`, scanParams);
      onStartScan(scanParams, selectedMode);
    }
    
    onClose();
  };
  
  const addScheduleTime = () => {
    setScheduleTimes([...scheduleTimes, '14:00']); // 2:00 PM default for additional times
  };
  
  const removeScheduleTime = (index) => {
    const newTimes = scheduleTimes.filter((_, i) => i !== index);
    setScheduleTimes(newTimes);
  };
  
  const updateScheduleTime = (index, value) => {
    const newTimes = [...scheduleTimes];
    newTimes[index] = value;
    setScheduleTimes(newTimes);
  };

  // Convert 12-hour time with AM/PM to 24-hour format
  const convertTo24Hour = (hour12, minute, period) => {
    let hour24 = parseInt(hour12);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minute.padStart(2, '0')}`;
  };
  
  // Convert 24-hour format to 12-hour with AM/PM
  const convertTo12Hour = (time24) => {
    const [hour24, minute] = time24.split(':');
    let hour12 = parseInt(hour24);
    let period = 'AM';
    
    if (hour12 >= 12) {
      period = 'PM';
      if (hour12 > 12) hour12 -= 12;
    } else if (hour12 === 0) {
      hour12 = 12;
    }
    
    return { hour: hour12.toString(), minute, period };
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-lg shadow-2xl max-w-3xl w-full my-4 sm:my-8 max-h-[95vh] overflow-y-auto"
        >
          {/* Header */}
          <div className={`px-3 sm:px-6 py-3 sm:py-4 border-b ${isBreakout ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} rounded-t-lg flex items-center justify-between`}>
            <div className="flex items-center gap-2 sm:gap-3">
              <FaCog className={`text-lg sm:text-2xl ${isBreakout ? 'text-green-600' : 'text-red-600'}`} />
              <div>
                <h2 className="text-base sm:text-xl font-bold text-gray-900">
                  Modify {isBreakout ? 'Breakout' : 'Breakdown'} Scan
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  Customize your trading parameters
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Broker Connection Error Alert */}
          {/* Only show if there's an error AND either:
              1. The error is NOT about expired token, OR
              2. The page-level session expired modal is NOT being shown
          */}
          {brokerError && !(hideSessionExpiredError && brokerError.includes('expired')) && (
            <div className="mx-3 sm:mx-6 mt-3 sm:mt-4 p-3 sm:p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
              <div className="flex items-start gap-2 sm:gap-3">
                <FaExclamationCircle className="text-red-500 text-lg sm:text-xl flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm sm:text-base font-semibold text-red-900 mb-1">
                    {brokerError.includes('expired') ? 'Access Token Expired' : 'Broker Not Connected'}
                  </h3>
                  <p className="text-xs sm:text-sm text-red-700 mb-2">{brokerError}</p>
                  <a 
                    href="/broker-integrations" 
                    className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-red-700 hover:text-red-900 underline"
                  >
                    {brokerError.includes('expired') ? 'Reconnect Broker →' : 'Connect Broker Account →'}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-3 sm:p-6">
            {/* Execution Mode Selection */}
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FaCog className="text-blue-600" />
                Execution Mode
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Automatic Mode */}
                <button
                  type="button"
                  onClick={() => setSelectedMode('automatic')}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedMode === 'automatic'
                      ? 'border-green-500 bg-green-50 shadow-md'
                      : 'border-gray-300 bg-white hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <FaRocket className={`text-lg ${selectedMode === 'automatic' ? 'text-green-600' : 'text-gray-500'}`} />
                    <span className={`font-semibold ${selectedMode === 'automatic' ? 'text-green-900' : 'text-gray-700'}`}>
                      Automatic
                    </span>
                    {selectedMode === 'automatic' && (
                      <FaCheckCircle className="ml-auto text-green-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    System places order for best candidate
                  </p>
                </button>

                {/* Manual Mode */}
                <button
                  type="button"
                  onClick={() => setSelectedMode('manual')}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedMode === 'manual'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-300 bg-white hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <FaHandPointer className={`text-lg ${selectedMode === 'manual' ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span className={`font-semibold ${selectedMode === 'manual' ? 'text-blue-900' : 'text-gray-700'}`}>
                      Manual Selection
                    </span>
                    {selectedMode === 'manual' && (
                      <FaCheckCircle className="ml-auto text-blue-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    Review top 3 and choose which to trade
                  </p>
                </button>
              </div>
            </div>
            {/* Two-Column Grid for Form Fields - Responsive */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-5">
              {/* Investment Amount */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Maximum Investment (₹)
                </label>
                <input
                  type="number"
                  value={maxInvestment}
                  onChange={(e) => setMaxInvestment(e.target.value)}
                  min="1000"
                  max="10000000"
                  step="1000"
                  required
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Max capital to deploy
                </p>
              </div>

              {/* Quantity/Lots */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Number of Lots
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min="1"
                  max="10"
                  step="1"
                  required
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lots to trade (1-10)
                </p>
              </div>

              {/* Target Profit % */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Target Profit (%)
                </label>
                <input
                  type="number"
                  value={targetPercent}
                  onChange={(e) => setTargetPercent(e.target.value)}
                  min="5"
                  max="100"
                  step="5"
                  required
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Profit target % (5-100%)
                </p>
              </div>

              {/* Stop-Loss % */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Stop-Loss (%)
                </label>
                <input
                  type="number"
                  value={stopLossPercent}
                  onChange={(e) => setStopLossPercent(e.target.value)}
                  min="2"
                  max="50"
                  step="1"
                  required
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Stop-loss % (2-50%)
                </p>
              </div>

              {/* Momentum Threshold Range */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Min {isBreakout ? 'Gain' : 'Loss'} (%)
                </label>
                <input
                  type="number"
                  value={momentumMin}
                  onChange={(e) => setMomentumMin(e.target.value)}
                  min="1"
                  max="10"
                  step="0.5"
                  required
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum threshold (1-10%)
                </p>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Max {isBreakout ? 'Gain' : 'Loss'} (%)
                </label>
                <input
                  type="number"
                  value={momentumMax}
                  onChange={(e) => setMomentumMax(e.target.value)}
                  min="1"
                  max="15"
                  step="0.5"
                  required
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum threshold (1-15%)
                </p>
              </div>
              
              {/* RSI Range (15-minute) */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  RSI Min (15-min)
                </label>
                <input
                  type="number"
                  value={rsiMin}
                  onChange={(e) => setRsiMin(e.target.value)}
                  min="10"
                  max="90"
                  step="5"
                  required
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {isBreakout ? 'Min RSI for entry (10-90)' : 'Avoid oversold below (10-90)'}
                </p>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  RSI Max (15-min)
                </label>
                <input
                  type="number"
                  value={rsiMax}
                  onChange={(e) => setRsiMax(e.target.value)}
                  min="10"
                  max="90"
                  step="5"
                  required
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {isBreakout ? 'Avoid overbought above (10-90)' : 'Max RSI for entry (10-90)'}
                </p>
              </div>
              
              {/* Option Moneyness */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Option Type
                </label>
                <select
                  value={optionMoneyness}
                  onChange={(e) => setOptionMoneyness(e.target.value)}
                  className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="ATM">ATM (At-the-Money)</option>
                  <option value="ITM">ITM (In-the-Money)</option>
                  <option value="OTM">OTM (Out-of-the-Money)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select option moneyness preference
                </p>
              </div>
            </div>

            {/* Scheduling Section */}
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2">
                  <FaClock className="text-blue-600 text-sm sm:text-base" />
                  <label className="text-xs sm:text-sm font-semibold text-gray-900">
                    Schedule Scan (Optional)
                  </label>
                </div>
                <input
                  type="checkbox"
                  checked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              
              {scheduleEnabled && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 mb-2">
                    Add times when you want this scan to run automatically (Mon-Fri, IST timezone)
                  </p>
                  {selectedMode === 'manual' && (
                    <div className="bg-white border border-blue-200 rounded-lg p-2 mb-3">
                      <p className="text-xs text-blue-900">
                        ✉️ Manual selection schedules will email the top candidates each time they run so you can review them quickly.
                      </p>
                    </div>
                  )}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
                    <p className="text-xs text-blue-800">
                      <strong>Example:</strong> For market open, set 9:30 AM. For mid-day, set 2:00 PM.
                    </p>
                  </div>
                  {scheduleTimes.map((time, index) => {
                    const { hour, minute, period } = convertTo12Hour(time);
                    
                    return (
                      <div key={index} className="flex items-center gap-1 sm:gap-2">
                        <div className="flex-1 flex items-center gap-1 sm:gap-2">
                          {/* Hour Input */}
                          <input
                            type="number"
                            value={hour}
                            onChange={(e) => {
                              const newTime = convertTo24Hour(e.target.value, minute, period);
                              updateScheduleTime(index, newTime);
                            }}
                            min="1"
                            max="12"
                            className="w-12 sm:w-16 px-1 sm:px-2 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm text-center"
                            placeholder="HH"
                          />
                          <span className="text-gray-600 font-bold text-xs sm:text-sm">:</span>
                          {/* Minute Input */}
                          <input
                            type="number"
                            value={minute}
                            onChange={(e) => {
                              const newTime = convertTo24Hour(hour, e.target.value.padStart(2, '0'), period);
                              updateScheduleTime(index, newTime);
                            }}
                            min="0"
                            max="59"
                            step="5"
                            className="w-12 sm:w-16 px-1 sm:px-2 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm text-center"
                            placeholder="MM"
                          />
                          {/* AM/PM Selector */}
                          <select
                            value={period}
                            onChange={(e) => {
                              const newTime = convertTo24Hour(hour, minute, e.target.value);
                              updateScheduleTime(index, newTime);
                            }}
                            className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-semibold bg-blue-50 text-blue-900"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                        {scheduleTimes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeScheduleTime(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          >
                            <FaTrash className="text-sm" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={addScheduleTime}
                    className="w-full py-2 text-xs sm:text-sm text-blue-600 hover:bg-blue-100 border border-blue-300 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <FaPlus className="text-xs" />
                    Add Another Time
                  </button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={connectedBrokers.length === 0 || brokerLoading}
                className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-semibold text-white transition-all text-sm sm:text-base ${
                  connectedBrokers.length === 0 || brokerLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : isBreakout
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                    : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700'
                } flex items-center justify-center gap-2`}
                title={connectedBrokers.length === 0 ? 'Please connect your broker account first' : ''}
              >
                {selectedMode === 'automatic' ? (
                  <FaRocket className="text-sm sm:text-base" />
                ) : (
                  <FaHandPointer className="text-sm sm:text-base" />
                )}
                <span className="hidden sm:inline">
                  {brokerLoading 
                    ? 'Checking Broker...' 
                    : selectedMode === 'automatic'
                    ? `Start Auto ${isBreakout ? 'Breakout' : 'Breakdown'} Scan`
                    : 'Scan for Manual Selection'
                  }
                </span>
                <span className="sm:hidden">
                  {brokerLoading ? 'Checking...' : selectedMode === 'automatic' ? 'Auto Scan' : 'Manual Scan'}
                </span>
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ModifyScanModal;

